import { httpClient } from '@bs/core'
import { getMomentTimezone } from './momentTimezone'

const DASHBOARD_URL = '/stores/dashboard'

// ---------------------------------------------------------------------------
// Request param types
// ---------------------------------------------------------------------------

export interface GeneralDashboardParams {
  startDate: string
  endDate: string
  locationId?: string | null
  source?: string
  netRevenue?: boolean
}

export interface SixDaysAgoParams {
  locationId?: string | null
  source?: string
  netRevenue?: boolean
}

export interface WebTrafficParams {
  startDate: string
  endDate: string
}

// ---------------------------------------------------------------------------
// Response types
// httpClient uses defaultResponseHandler which unwraps { data, status, message }
// — these types describe the already-unwrapped inner data object.
// ---------------------------------------------------------------------------

export interface KpiMetric {
  current: number | null
  previous: number | null
  percentage_increase: number | null
}

export interface TopProduct {
  id: number
  name: string
  price: string
  regular_price: string
  stock_quantity: number
  stock_status: string
  image: { src: string }
  parent_id: number
  sold_quantity: number
  avg_sold_price: number
  avg_profit: number
  revenue: number
  profit: number
}

export interface TopCategory {
  id: number
  name: string
  revenue: number
}

export interface LocationPerformance {
  location_id: number | string
  location_name: string
  revenue: number
  profit: number
  margin: number
  number_of_orders: number
  number_of_pos_orders: number
  number_of_online_orders: number
  top_shipping_method_by_number_of_orders: string
}

export interface LocationRevenue {
  location_id: number
  location_name: string
  headers: string[]
  rows: [string, number][]
}

export interface DailyTimeSeries {
  headers: string[]
  rows: [string, number, number][]
}

// online_revenue / pos_revenue may arrive either as a plain number or as the
// same { current, previous, percentage_increase } shape used by other KPIs.
// Mappers normalize both — see numFromMetric / trendFromMetric.
export type RevenueValue = number | KpiMetric

export interface GeneralDashboardResponse {
  revenue: KpiMetric
  cogs: KpiMetric
  profit: KpiMetric
  number_of_orders: KpiMetric
  average_order_amount: KpiMetric
  number_of_products_sold: KpiMetric
  average_cart_size: KpiMetric
  discount: KpiMetric
  average_daily_revenue: KpiMetric
  average_daily_orders: KpiMetric
  customers: { new: number; returning: number; new_percentage: number }
  top_products: TopProduct[]
  top_categories: TopCategory[]
  top_brands: unknown[]
  top_coupons: unknown[]
  top_budtenders: { id: number; name: string; number_of_orders: number; revenue: number }[]
  locations_performance: LocationPerformance[]
  locations_revenue: LocationRevenue[]
  // headers: ["date", "customer_pickup_revenue", "local_delivery_revenue"]
  shipping_method_revenue_by_day: DailyTimeSeries
  // headers: ["date", "number_of_online_orders", "number_of_pos_orders"]
  number_of_orders_by_day: DailyTimeSeries
  // Clean per-channel revenue (added to read_general).
  // Total online revenue for the period (delivery + pickup online orders).
  online_revenue?: RevenueValue
  // Total in-store / POS revenue for the period.
  pos_revenue?: RevenueValue
  // Per-day online vs in-store revenue.
  // headers: ["date", "online_revenue", "pos_revenue"] → rows: [date, online, pos]
  revenue_by_day?: DailyTimeSeries
  // Conversion rate as a percent (0..100, e.g. 30.0) — already scaled, do NOT ×100.
  // Absent for non-pantry stores.
  conversion_rate?: KpiMetric
  // Total sessions (count) for the period. Absent for non-pantry stores.
  // Distinct from the GA4 web-traffic `sessions` feed.
  total_sessions?: KpiMetric
}

// headers: ["orders_count", "revenue", "profit", "new_customers"]
// data: 7 rows (one per day, last 7 days)
export interface SixDaysAgoResponse {
  headers: string[]
  data: number[][]
}

// headers: ["channelGrouping", "returningUsers", "newUsers", "sessions",
//           "avgSessionDuration", "pageviewsPerSession", "avgTimeOnPage", "goalConversionRateAll"]
// data: one row per channel group
export interface WebTrafficResponse {
  headers: string[]
  data: string[][]
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

export const fetchGeneralDashboard = (params: GeneralDashboardParams): Promise<GeneralDashboardResponse> =>
  httpClient.json.post(DASHBOARD_URL, {
    action: 'read_general',
    parameters: {
      timezone: getMomentTimezone() || undefined,
      date_gte: params.startDate,
      date_lte: params.endDate,
      ...(params.locationId && params.locationId !== 'all' ? { location_id: Number(params.locationId) } : {}),
      filters: {
        ...(params.source ? { source: params.source } : {}),
        ...(params.netRevenue !== undefined ? { net_revenue: params.netRevenue } : {}),
      },
    },
  }) as Promise<GeneralDashboardResponse>

export const fetchSixDaysAgo = (params: SixDaysAgoParams): Promise<SixDaysAgoResponse> =>
  httpClient.json.post(DASHBOARD_URL, {
    action: 'read_from_six_days_ago',
    parameters: {
      timezone: getMomentTimezone() || undefined,
      ...(params.locationId && params.locationId !== 'all' ? { location_id: Number(params.locationId) } : {}),
      filters: {
        ...(params.source ? { source: params.source } : {}),
        ...(params.netRevenue !== undefined ? { net_revenue: params.netRevenue } : {}),
      },
    },
  }) as Promise<SixDaysAgoResponse>

export const fetchWebTraffic = (params: WebTrafficParams): Promise<WebTrafficResponse> =>
  httpClient.json.post(DASHBOARD_URL, {
    action: 'read_web_traffic',
    parameters: {
      timezone: getMomentTimezone() || undefined,
      start_date: params.startDate,
      end_date: params.endDate,
    },
  }) as Promise<WebTrafficResponse>
